/**
 * Serviço de autenticação biométrica usando Web Authentication API (WebAuthn).
 * Utiliza verificação local do dispositivo (impressão digital / Face ID).
 */

import LOCAL_STORAGE from "@/constants/local-storage";

const RELYING_PARTY_NAME = "Talk2Buy";
const RELYING_PARTY_ID = typeof window !== "undefined" ? window.location.hostname : "localhost";

function generateChallenge(): ArrayBuffer {
  const challenge = new Uint8Array(32);
  crypto.getRandomValues(challenge);
  return challenge.buffer as ArrayBuffer;
}

function bufferToBase64(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}

function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

const biometricAuthService = {
  /**
   * Verifica se o dispositivo suporta autenticação biométrica via plataforma
   */
  async isAvailable(): Promise<boolean> {
    if (typeof window === "undefined") return false;
    if (!window.PublicKeyCredential) return false;

    try {
      return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  },

  /**
   * Verifica se biometria está ativada para este dispositivo
   */
  isEnabled(): boolean {
    return localStorage.getItem(LOCAL_STORAGE.biometricEnabled) === "true" &&
      !!localStorage.getItem(LOCAL_STORAGE.biometricCredentialId);
  },

  /**
   * Registra credencial biométrica vinculada ao dispositivo
   */
  async register(userId: string, userName: string): Promise<boolean> {
    try {
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: generateChallenge(),
          rp: {
            name: RELYING_PARTY_NAME,
            id: RELYING_PARTY_ID,
          },
          user: {
            id: new TextEncoder().encode(userId).buffer as ArrayBuffer,
            name: userName,
            displayName: userName,
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },   // ES256
            { alg: -257, type: "public-key" },  // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",
            userVerification: "required",
            residentKey: "preferred",
          },
          timeout: 60000,
          attestation: "none",
        },
      }) as PublicKeyCredential | null;

      if (!credential) return false;

      const credentialId = bufferToBase64(credential.rawId);
      localStorage.setItem(LOCAL_STORAGE.biometricCredentialId, credentialId);
      localStorage.setItem(LOCAL_STORAGE.biometricEnabled, "true");

      return true;
    } catch (error) {
      console.error("[BiometricAuth] Registration failed:", error);
      return false;
    }
  },

  /**
   * Solicita verificação biométrica para desbloquear
   */
  async authenticate(): Promise<boolean> {
    const credentialId = localStorage.getItem(LOCAL_STORAGE.biometricCredentialId);
    if (!credentialId) return false;

    try {
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: generateChallenge(),
          rpId: RELYING_PARTY_ID,
          allowCredentials: [
            {
              id: base64ToBuffer(credentialId) as ArrayBuffer,
              type: "public-key",
              transports: ["internal"],
            },
          ],
          userVerification: "required",
          timeout: 60000,
        },
      });

      return !!assertion;
    } catch (error) {
      console.error("[BiometricAuth] Authentication failed:", error);
      return false;
    }
  },

  /**
   * Desativa biometria
   */
  disable(): void {
    localStorage.removeItem(LOCAL_STORAGE.biometricCredentialId);
    localStorage.removeItem(LOCAL_STORAGE.biometricEnabled);
  },
};

export default biometricAuthService;
